import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { unsubscribeFn } from "@/lib/newsletter/newsletter.functions";

export const Route = createFileRoute("/unsubscribe")({
  component: UnsubscribePage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search["token"] === "string" ? (search["token"] as string) : "",
  }),
  head: () => ({
    meta: [
      { title: "Unsubscribe — CB16 Journal" },
      {
        name: "description",
        content: "Manage your CB16 journal email preferences and unsubscribe from the newsletter.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Unsubscribe — CB16 Journal" },
      { property: "og:description", content: "Manage your CB16 journal email preferences." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function UnsubscribePage() {
  const { token } = Route.useSearch();
  const [state, setState] = useState<"working" | "done" | "error">("working");
  const [email, setEmail] = useState("");

  useEffect(() => {
    let active = true;
    if (!token) {
      setState("error");
      return;
    }
    unsubscribeFn({ data: { token } })
      .then((r) => {
        if (!active) return;
        if (r.ok) {
          setEmail(r.email ?? "");
          setState("done");
        } else setState("error");
      })
      .catch(() => active && setState("error"));
    return () => {
      active = false;
    };
  }, [token]);

  return (
    <main className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center px-6 py-24 text-center">
      <span className="text-[10px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">
        Le Journal CB16
      </span>
      <h1
        className="mt-4 text-3xl font-normal text-foreground"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        {state === "working" && "Updating your preferences…"}
        {state === "done" && "You have been unsubscribed"}
        {state === "error" && "We could not verify this link"}
      </h1>
      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
        {state === "done" &&
          `${email ? email + " " : "You "}will no longer receive our journal updates. Your account and order history remain untouched.`}
        {state === "error" &&
          "This unsubscribe link is invalid or has already been used. Please contact us if you keep receiving emails."}
        {state === "working" && "One moment while we update your subscription."}
      </p>
      <Link
        to="/"
        className="mt-8 inline-flex items-center justify-center bg-[#C6A15B] px-8 py-3 text-[11px] font-medium uppercase tracking-[0.28em] text-black transition hover:opacity-90"
      >
        Return to CB16
      </Link>
    </main>
  );
}